import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2Icon,
  AlertTriangleIcon,
  RefreshCwIcon,
  EyeIcon,
  EyeOffIcon,
  CopyIcon,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { gatewayApi } from '@nebula/shared/services'

import SerialConsole from '../SerialConsole'

type ConsoleTabProps = {
  initialUser?: string | null
  initialPassword?: string | null
}

export default function ConsoleTab({ initialUser, initialPassword }: ConsoleTabProps) {
  const queryClient = useQueryClient()
  const [showPassword, setShowPassword] = useState(false)

  const {
    data: ticket,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['gateway-console-ticket'],
    queryFn: gatewayApi.getGatewayConsoleTicket,
    staleTime: 50 * 60 * 1000, // 50 min (ticket expira em 1h)
    retry: 1,
  })

  const handleReconnect = () => {
    queryClient.invalidateQueries({ queryKey: ['gateway-console-ticket'] })
  }

  if (isLoading) {
    return (
      <div className="flex h-[500px] items-center justify-center rounded-lg bg-gray-900">
        <div className="text-center">
          <Loader2Icon className="mx-auto mb-3 size-8 animate-spin text-blue-400" />
          <p className="text-sm text-gray-300">Conectando ao console...</p>
        </div>
      </div>
    )
  }

  if (error || !ticket?.websocket_url) {
    return (
      <div className="flex h-[500px] items-center justify-center rounded-lg bg-gray-900">
        <div className="text-center">
          <AlertTriangleIcon className="mx-auto mb-3 size-8 text-yellow-400" />
          <p className="mb-1 text-sm text-gray-300">Erro ao obter ticket do console</p>
          <p className="mb-4 text-xs text-gray-500">
            {error instanceof Error ? error.message : 'Tente novamente'}
          </p>
          <button
            onClick={handleReconnect}
            className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700"
          >
            <RefreshCwIcon className="size-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">Console serial do gateway</p>
        <button
          onClick={handleReconnect}
          className="inline-flex items-center gap-1.5 rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
        >
          <RefreshCwIcon className="size-3.5" />
          Reconectar
        </button>
      </div>
      {initialUser && initialPassword && (
        <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
          <div className="flex items-center gap-1.5 text-gray-500">
            <span>Usuário:</span>
            <code className="rounded border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-gray-900">
              {initialUser}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(initialUser)
                toast.success('Usuário copiado!')
              }}
              className="text-gray-400 hover:text-gray-600"
              title="Copiar usuário"
            >
              <CopyIcon className="size-3" />
            </button>
          </div>
          <div className="flex items-center gap-1.5 text-gray-500">
            <span>Senha:</span>
            <code className="rounded border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-gray-900">
              {showPassword ? initialPassword : '••••••••••••'}
            </code>
            <button
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600"
              title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOffIcon className="size-3" /> : <EyeIcon className="size-3" />}
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(initialPassword)
                toast.success('Senha copiada!')
              }}
              className="text-gray-400 hover:text-gray-600"
              title="Copiar senha"
            >
              <CopyIcon className="size-3" />
            </button>
          </div>
        </div>
      )}
      <div className="h-[500px] overflow-hidden rounded-lg border border-gray-700">
        <SerialConsole websocketUrl={ticket.websocket_url} />
      </div>
    </div>
  )
}
